import { useMemo, useState } from 'react'
import { Download, RefreshCw } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  DataTable,
  EmptyState,
  ExportDrawer,
  MetricCard,
  MetricStrip,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  groupRows,
} from '@/components/ui'
import { useInventory } from '../api/queries'
import type { InventoryRow } from '../api/types'
import { INVENTORY_COLUMNS } from '../utils/csv'

function daysUntilSellBy(sellBy: string | null): number | null {
  if (!sellBy) return null
  const diff = new Date(sellBy).getTime() - Date.now()
  return Math.ceil(diff / 86_400_000)
}

export function UvivaInventoryPage() {
  const [search, setSearch] = useState('')
  const [onlyAvailable, setOnlyAvailable] = useState(false)
  const [productFilter, setProductFilter] = useState('')
  const [exportOpen, setExportOpen] = useState(false)

  const { data, isLoading, isFetching, isError, refetch } = useInventory({ onlyAvailable })

  // Distinct products for the filter dropdown
  const productOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of data?.rows ?? []) {
      const key = r.product_code || r.product_name
      if (!seen.has(key)) seen.set(key, r.product_code ? `${r.product_code}  ${r.product_name}` : r.product_name)
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [data])

  const rows = useMemo(() => {
    let result = data?.rows ?? []
    if (productFilter) result = result.filter((r) => (r.product_code || r.product_name) === productFilter)
    const needle = search.trim().toLowerCase()
    if (needle) result = result.filter(
      (r) =>
        String(r.product_code ?? '').toLowerCase().includes(needle) ||
        String(r.product_name ?? '').toLowerCase().includes(needle) ||
        (r.lot_serial?.toLowerCase().includes(needle) ?? false),
    )
    return result
  }, [data, search, productFilter])

  const totalOnHand = useMemo(
    () => rows.reduce((s, r) => s + r.on_hand, 0),
    [rows],
  )
  const expiringSoon = useMemo(
    () => rows.filter((r) => { const d = daysUntilSellBy(r.sell_by); return d !== null && d <= 90 }).length,
    [rows],
  )

  const groups = useMemo(() => {
    if (productFilter) return undefined  // single product selected — flat table
    return groupRows(rows, {
      keyOf: (r) => r.product_code || r.product_name,
      labelOf: (key) => {
        const match = rows.find((r) => (r.product_code || r.product_name) === key)
        return match
          ? match.product_code
            ? `${match.product_code}  ${match.product_name}`
            : match.product_name
          : key
      },
    })
  }, [rows, productFilter])

  const columns = useMemo<ColumnDef<InventoryRow>[]>(
    () => [
      {
        id: 'product_code',
        header: 'Code',
        accessorKey: 'product_code',
        cell: (ctx) => (
          <span className="text-role-data-primary">{ctx.getValue() as string}</span>
        ),
      },
      {
        id: 'product_name',
        header: 'Product',
        accessorKey: 'product_name',
        cell: (ctx) => (
          <span className="text-role-data-secondary">{ctx.getValue() as string}</span>
        ),
      },
      {
        id: 'lot_serial',
        header: 'Lot / Serial',
        accessorKey: 'lot_serial',
        cell: (ctx) => (
          <span className="text-role-table-cell">{(ctx.getValue() as string | null) ?? '—'}</span>
        ),
      },
      {
        id: 'on_hand',
        header: 'On Hand',
        accessorKey: 'on_hand',
        cell: (ctx) => (
          <span className="text-role-table-cell">{(ctx.getValue() as number).toLocaleString('en-US', { maximumFractionDigits: 1 })}</span>
        ),
      },
      {
        id: 'available',
        header: 'Available',
        accessorKey: 'available',
        cell: (ctx) => {
          const v = ctx.getValue() as number
          return (
            <span className={`text-role-table-cell ${v <= 0 ? 'text-danger' : ''}`}>
              {v.toLocaleString('en-US', { maximumFractionDigits: 1 })}
            </span>
          )
        },
      },
      {
        id: 'uom',
        header: 'UoM',
        accessorKey: 'uom',
        cell: (ctx) => (
          <span className="text-role-metadata">{ctx.getValue() as string}</span>
        ),
      },
      {
        id: 'sell_by',
        header: 'Sell By',
        accessorKey: 'sell_by',
        cell: (ctx) => {
          const v = ctx.getValue() as string | null
          if (!v) return <span className="text-role-metadata">—</span>
          const days = daysUntilSellBy(v)
          const soon = days !== null && days <= 90
          return (
            <span className={`text-role-metadata ${soon ? 'text-warning font-medium' : ''}`}>
              {v}
            </span>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col h-full bg-canvas">
      {/* Header band */}
      <header className="flex items-center justify-between gap-6 border-b border-border-subtle bg-surface px-6 py-3">
        <div className="flex items-center gap-4">
          <h1 className="text-role-page-title">UVWH Inventory</h1>
          {data && (
            <MetricStrip variant="inline">
              <MetricCard variant="inline" label="Total rows" value={rows.length} />
              <MetricCard variant="inline" label="On hand (filtered)" value={totalOnHand.toLocaleString('en-US', { maximumFractionDigits: 1 })} />
              {expiringSoon > 0 && (
                <MetricCard
                  variant="inline"
                  label="Expiring ≤90d"
                  value={expiringSoon}
                  valueTone="warning"
                />
              )}
            </MetricStrip>
          )}
          {data?.cached_at && (
            <span className="text-role-metadata">
              As of {new Date(data.cached_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw size={12} />
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </header>

      {/* Search bar — own full-width row */}
      <div className="border-b border-border-subtle bg-surface px-6 py-2">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search product code, name, lot…"
          containerClassName="flex-1 max-w-none"
        />
      </div>

      <Toolbar className="border-b border-border-subtle bg-surface px-6 py-2">
        <ToolbarLeft>
          <Select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="w-64"
          >
            <option value="">All products</option>
            {productOptions.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </Select>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-secondary">
            <input
              type="checkbox"
              checked={onlyAvailable}
              onChange={(e) => setOnlyAvailable(e.target.checked)}
              className="rounded border-border"
            />
            Available only
          </label>
        </ToolbarLeft>
        <ToolbarRight>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExportOpen(true)}
            disabled={rows.length === 0}
            title="Pick columns or apply a saved template before download"
          >
            <Download size={12} /> Export…
          </Button>
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-3">
        {isLoading ? (
          <div className="text-role-metadata py-8 text-center">Loading inventory…</div>
        ) : isError ? (
          <EmptyState
            variant="error"
            title="Could not load inventory"
            description="Check that Uviva is configured in Settings and try again."
            action={<Button variant="ghost" size="sm" onClick={() => refetch()}>Retry</Button>}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="no-data"
            title="No stock found"
            description={
              onlyAvailable
                ? 'No available inventory in UVWH for your products.'
                : 'No inventory found in the UVWH warehouse.'
            }
            action={
              onlyAvailable ? (
                <Button variant="ghost" size="sm" onClick={() => setOnlyAvailable(false)}>
                  Show all
                </Button>
              ) : undefined
            }
          />
        ) : (
          <DataTable
            moduleId="uviva-inventory"
            columns={columns}
            data={rows}
            groups={groups}
            maxHeight="100%"
            criticalRowPredicate={(row) => {
              const days = daysUntilSellBy(row.sell_by)
              return days !== null && days <= 90
            }}
          />
        )}
      </div>

      <ExportDrawer
        open={exportOpen}
        onOpenChange={setExportOpen}
        rows={rows}
        columns={INVENTORY_COLUMNS}
        moduleId="uviva-inventory-export"
        filenameBase="uviva-inventory"
      />
    </div>
  )
}
